import { ORG_TIPO, PERFIL_COMPRADOR } from "./schema.js";

/**
 * Organizações, na visão da PLATAFORMA (§B).
 *
 * ⚠️ O QUE ESTA TELA NÃO FAZ: criar conta ou senha. O usuário nasce sempre do
 * CLAIM, pelo link que prova posse — `/v/` (romaneio), `/o/` (oferta), `/c/`
 * (carga). Criar credencial no lugar de outra pessoa produz organização que
 * ninguém reivindicou e senha que alguém precisa transmitir por WhatsApp.
 *
 * O que ela faz é PRÉ-CADASTRAR a organização, sem usuário. Hoje um comprador só
 * existe se alguém formalizou um romaneio para ele; a praça precisa do contrário
 * — "toda a praça de Mirorós (11 compradores)" só é verdade se os 11 existirem
 * como organização antes de qualquer um ter conta.
 */

/** Uma linha da lista. */
export interface OrganizacaoDTO {
  id: string;
  nome: string;
  nomeFantasia: string | null;
  slug: string;
  tipo: string;
  /** CORRETOR | DISTRIBUIDOR. `null` em org PRODUTOR. */
  perfilComprador: string | null;
  cidade: string | null;
  /** Dígitos sem o 55 — a mesma chave do login e do claim. */
  telefoneWhatsapp: string | null;
  pracas: string[];
  modulesEnabled: string[];
  /**
   * Quantos usuários. **0 = pré-cadastrada**, e é isso que o chip âmbar "sem
   * acesso" diz. Derivado, nunca coluna — ver `orgPreCadastradaPorTelefone`.
   */
  usuarios: number;
  ativo: boolean;
  criadoEm: string;
}

export interface NovaOrganizacao {
  nome: string;
  tipo: string;
  perfilComprador?: string | null;
  telefoneWhatsapp: string;
  cidade?: string | null;
  nomeFantasia?: string | null;
  pracas?: string[];
  modulesEnabled?: string[];
}

export interface PatchOrganizacao {
  nome?: string;
  nomeFantasia?: string | null;
  perfilComprador?: string | null;
  telefoneWhatsapp?: string | null;
  cidade?: string | null;
  pracas?: string[];
  modulesEnabled?: string[];
}

/**
 * Os módulos que cada tipo nasce com.
 *
 * ⚠️ COMPRADOR nasce só com `mercado` — é o que o claim já faz (`executarClaim`),
 * e ter Cacho no menu de quem não tem plantação é oferecer telas que respondem
 * 403. PRODUTOR nasce com o mesmo default do signup, que é o do trial.
 */
export const MODULOS_PADRAO_POR_TIPO: Record<string, string[]> = {
  [ORG_TIPO.COMPRADOR]: ["mercado"],
  [ORG_TIPO.PRODUTOR]: ["cacho", "equipe"],
};

export function modulosPadraoDoTipo(tipo: string): string[] {
  return MODULOS_PADRAO_POR_TIPO[tipo] ?? ["cacho"];
}

/** "Produtor" · "Comprador" — e o valor cru para um tipo que eu não conheço. */
export function labelOrgTipo(tipo: string | null | undefined): string {
  if (tipo === ORG_TIPO.COMPRADOR) return "Comprador";
  if (tipo === ORG_TIPO.PRODUTOR) return "Produtor";
  // ⚠️ Não cai em "Produtor" no default. `tipo` só tem dois valores hoje (ver o
  // comentário de `ORG_TIPO`), e o dia em que ganhar um terceiro, mostrar o valor
  // cru é uma tela estranha; chamar de Produtor é uma tela ERRADA, e ninguém
  // descobriria olhando.
  return tipo ? tipo.toLowerCase() : "—";
}

/**
 * A ORDEM da lista: sem acesso primeiro, depois por nome.
 *
 * ⚠️ O que precisa de atenção fica em cima. Organização pré-cadastrada é uma
 * promessa pela metade — ela existe para que alguém a reivindique —, e enterrá-la
 * na ordem alfabética entre trinta orgs com conta é perdê-la de vista.
 */
export function ordenarOrganizacoes(orgs: OrganizacaoDTO[]): OrganizacaoDTO[] {
  return orgs.slice().sort((a, b) => {
    if ((a.usuarios === 0) !== (b.usuarios === 0)) return a.usuarios === 0 ? -1 : 1;
    return a.nome.localeCompare(b.nome, "pt-BR");
  });
}

/**
 * Minúsculas e SEM ACENTO — a comparação de busca.
 *
 * ⚠️ Quem digita procura "savio" e a org chama "Sávio Nunes". Busca sensível a
 * acento devolve vazio, e o admin conclui que a organização não existe — e
 * cadastra a segunda. Num app onde metade dos nomes tem acento, isso não é
 * conforto, é o caminho para a duplicata que o pré-cadastro existe para evitar.
 */
function comparavel(v: string): string {
  return v.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

/** O filtro da tela — busca por nome/fantasia/telefone e tipo. */
export function filtrarOrganizacoes(
  orgs: OrganizacaoDTO[],
  filtro: { q?: string; tipo?: string },
): OrganizacaoDTO[] {
  const q = comparavel((filtro.q ?? "").trim());
  const digitos = q.replace(/\D/g, "");
  return orgs.filter(o => {
    if (filtro.tipo && filtro.tipo !== "todos" && o.tipo !== filtro.tipo) return false;
    if (!q) return true;
    if (comparavel(o.nome).indexOf(q) >= 0) return true;
    if (comparavel(o.nomeFantasia ?? "").indexOf(q) >= 0) return true;
    // Busca por telefone só quando o termo TEM dígito: `digitos` vazio casaria
    // com todo telefone e a busca por nome pararia de filtrar nada.
    if (digitos && (o.telefoneWhatsapp ?? "").indexOf(digitos) >= 0) return true;
    return false;
  });
}

/**
 * A praça PADRÃO do produtor — a primeira das escolhidas.
 *
 * `praca` (singular) é o filtro que a tela de Cotações abre selecionado, e é do
 * PRODUTOR; `pracas` (lista) é onde o COMPRADOR atua. Ver as duas colunas no
 * schema. Comprador não tem praça padrão: a lista é que vale para ele.
 */
export function pracaPadraoDoTipo(tipo: string, pracas: string[]): string | null {
  if (tipo !== ORG_TIPO.PRODUTOR) return null;
  return pracas[0] ?? null;
}

/** Os valores que o formulário aceita — o servidor valida os mesmos. */
export const TIPOS_ORGANIZACAO = [ORG_TIPO.PRODUTOR, ORG_TIPO.COMPRADOR] as const;
export const PERFIS = [PERFIL_COMPRADOR.CORRETOR, PERFIL_COMPRADOR.DISTRIBUIDOR] as const;

export const ERRO_ORGANIZACAO = {
  TELEFONE_INVALIDO: "TELEFONE_INVALIDO",
  TELEFONE_EM_USO: "TELEFONE_EM_USO",
  NAO_ENCONTRADO: "NAO_ENCONTRADO",
  PERFIL_OBRIGATORIO: "PERFIL_OBRIGATORIO",
} as const;
